/**
 * CloseShiftPage — 7-step full-page Close Shift Wizard.
 *
 * Route: /close-shift?shift_id=SH-YYYY-MM-DD-xxx
 *
 * Steps:
 *   1 — Cash Sales This Shift
 *   2 — Customer Credit Generated This Shift
 *   3 — AR / Credit Payments Collected This Shift
 *   4 — Expenses This Shift
 *   5 — Actual Cash Count (Blind)
 *   6 — Shift Summary (Pre-close review)
 *   7 — Close & Lock Shift (Dual PIN → Done / X-Report)
 */
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Receipt, CreditCard, Banknote, ReceiptText, Calculator, ClipboardCheck,
  Lock, CheckCircle2, EyeOff, AlertTriangle, Printer, KeyRound, ShieldCheck,
  Users, TrendingUp, ArrowLeft,
} from 'lucide-react';

const STEPS = [
  { id: 1, title: 'Cash Sales',       icon: Receipt,        desc: 'Non-credit sales this shift' },
  { id: 2, title: 'Credit Created',   icon: CreditCard,     desc: 'Credit & partial invoices' },
  { id: 3, title: 'AR Collected',     icon: Banknote,       desc: 'AR payments received' },
  { id: 4, title: 'Expenses',         icon: ReceiptText,    desc: 'Expenses this shift' },
  { id: 5, title: 'Cash Count',       icon: Calculator,     desc: 'Blind count & variance' },
  { id: 6, title: 'Shift Summary',    icon: ClipboardCheck, desc: 'Pre-close review' },
  { id: 7, title: 'Close & Lock',     icon: Lock,           desc: 'Dual PIN sign-off' },
];

// ── Formatters ─────────────────────────────────────────────────────────────
const fmt = n => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtTime = iso => { try { return new Date(iso).toLocaleString(); } catch { return '—'; } };

// ── Payment-type badge ──────────────────────────────────────────────────────
function PMBadge({ type }) {
  const t = (type || '').toLowerCase();
  const map = {
    cash:    'bg-emerald-100 text-emerald-700',
    credit:  'bg-amber-100 text-amber-700',
    digital: 'bg-blue-100 text-blue-700',
    split:   'bg-purple-100 text-purple-700',
    partial: 'bg-orange-100 text-orange-700',
    check:   'bg-cyan-100 text-cyan-700',
  };
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${map[t] || 'bg-slate-100 text-slate-600'}`}>
      {type || '—'}
    </span>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────
export default function CloseShiftPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shiftId = searchParams.get('shift_id');

  const [step, setStep]     = useState(1);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);

  // Step 5 — blind count
  const [cashCounted, setCashCounted]           = useState('');
  const [varianceRevealed, setVarianceRevealed] = useState(false);
  const [reason, setReason]                     = useState('');

  // Step 7 — dual PIN
  const [pin, setPin]               = useState('');
  const [managerPin, setManagerPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [finalReport, setFinalReport] = useState(null);

  const isDone = !!finalReport;

  // Load preview on mount
  useEffect(() => {
    if (!shiftId) { navigate('/shifts'); return; }
    api.get(`/shifts/${shiftId}/close-preview`)
      .then(res => {
        setPreview(res.data);
        // If already closed, jump straight to done state
        if (res.data.status === 'closed') {
          setFinalReport(res.data);
        }
      })
      .catch(() => toast.error('Failed to load shift data'))
      .finally(() => setLoading(false));
  }, [shiftId, navigate]);

  // Computed values
  const expectedCash = Number(preview?.cash_expected || 0);
  const counted      = parseFloat(cashCounted) || 0;
  const variance     = +(counted - expectedCash).toFixed(2);
  const vc           = variance === 0 ? 'text-emerald-700' : variance > 0 ? 'text-blue-700' : 'text-red-700';
  const vcBg         = variance === 0 ? 'bg-emerald-50 border-emerald-200' : variance > 0 ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200';
  const vcLabel      = variance === 0
    ? 'Balanced ✓'
    : variance > 0
      ? `+₱${fmt(Math.abs(variance))} (over)`
      : `-₱${fmt(Math.abs(variance))} (short)`;

  const canStep5Proceed = varianceRevealed && cashCounted !== '' && (variance === 0 || reason.trim());

  const submit = async () => {
    if (!pin.trim() || !managerPin.trim()) { toast.error('Both PINs are required.'); return; }
    if (pin.trim() === managerPin.trim())  { toast.error('Manager PIN must differ from cashier PIN.'); return; }
    if (variance !== 0 && !reason.trim())  { toast.error('Variance reason is required.'); return; }
    setSubmitting(true);
    try {
      const res = await api.post(`/shifts/${shiftId}/close`, {
        cash_counted: counted,
        cashier_pin: pin.trim(),
        manager_pin: managerPin.trim(),
        variance_reason: reason.trim() || undefined,
      });
      setFinalReport(res.data);
      toast.success('Shift closed — dual-signature locked.');
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Failed to close shift');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Guards ─────────────────────────────────────────────────────────────────
  if (!shiftId) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center">
        <p className="text-slate-400 text-sm">Loading shift data…</p>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex flex-col items-center justify-center gap-3">
        <p className="text-slate-500">Shift not found or access denied.</p>
        <Button variant="outline" onClick={() => navigate('/shifts')}>
          <ArrowLeft size={14} className="mr-1.5" /> Back to Shifts
        </Button>
      </div>
    );
  }

  // ── DONE STATE ─────────────────────────────────────────────────────────────
  if (isDone) {
    const fr = finalReport;
    const varClass = (fr.cash_variance || 0) === 0 ? 'text-emerald-700'
      : (fr.cash_variance || 0) > 0 ? 'text-blue-700' : 'text-red-700';
    const varBg = (fr.cash_variance || 0) === 0 ? 'bg-emerald-50 border-emerald-200'
      : (fr.cash_variance || 0) > 0 ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200';
    const varBadge = (fr.cash_variance || 0) === 0 ? 'bg-emerald-100 text-emerald-700'
      : (fr.cash_variance || 0) > 0 ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700';

    return (
      <div className="min-h-screen bg-[#F5F5F0]">
        <div className="bg-white border-b border-slate-200 px-4 py-3">
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="p-1.5 rounded-md text-slate-500 hover:text-slate-800 hover:bg-slate-100">
              <ArrowLeft size={16} />
            </button>
            <h1 className="text-sm font-semibold text-slate-800">Shift Closed — X-Report</h1>
          </div>
        </div>
        <div className="max-w-2xl mx-auto p-4 space-y-4">
          {/* Success banner */}
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-3" data-testid="final-x-report">
            <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
              <CheckCircle2 size={20} className="text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-emerald-800">Shift Closed Successfully</p>
              <p className="text-xs text-emerald-700 mt-0.5">{fr.shift_id} · {fr.cashier_name}</p>
            </div>
          </div>

          {/* Variance badge */}
          <div className={`rounded-xl border p-4 flex items-center justify-between ${varBg}`}>
            <div>
              <p className="text-[11px] text-slate-500 uppercase tracking-wide">Final Variance</p>
              <p className={`text-2xl font-bold  mt-0.5 ${varClass}`}>
                {(fr.cash_variance || 0) > 0 ? '+' : ''}₱{fmt(Math.abs(fr.cash_variance || 0))}
              </p>
            </div>
            <Badge className={varBadge}>
              {(fr.cash_variance || 0) === 0 ? 'Balanced' : (fr.cash_variance || 0) > 0 ? 'Over' : 'Short'}
            </Badge>
          </div>

          {/* X-Report snapshot */}
          <div className="rounded-lg border bg-white overflow-hidden" style={{ borderColor: 'var(--border-default)' }}>
            <div className="px-4 py-2.5 border-b flex items-center gap-2" style={{ backgroundColor: '#F1F0ED', borderColor: 'var(--border-subtle)' }}>
              <TrendingUp size={14} style={{ color: 'var(--text-secondary)' }} />
              <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>X-Report Snapshot</span>
            </div>
            <div className="p-4 space-y-1.5">
              <XRow label="Cashier" value={fr.cashier_name} />
              <XRow label="Shift ID" value={fr.shift_id} />
              <XRow label="Opened" value={fmtTime(fr.opened_at)} />
              <XRow label="Closed" value={fmtTime(fr.closed_at)} />
              <div className="border-t border-slate-100 my-1" />
              <XRow label="Opening Float" value={`₱${fmt(fr.opening_float)}`} />
              <XRow label="Gross Sales" value={`₱${fmt(fr.total_gross_sales)}`} bold />
              <XRow label="Invoices" value={String(fr.total_invoice_count)} />
              {(fr.epay_grand_total || 0) > 0 && <XRow label="Digital Total" value={`₱${fmt(fr.epay_grand_total)}`} />}
              <div className="border-t border-slate-100 my-1" />
              <XRow label="Expected Cash" value={`₱${fmt(fr.cash_expected)}`} />
              <XRow label="Counted Cash" value={`₱${fmt(fr.cash_counted)}`} />
              <XRow label="Variance" value={`${(fr.cash_variance || 0) >= 0 ? '+' : ''}₱${fmt(fr.cash_variance || 0)}`} bold />
              {fr.variance_reason && <XRow label="Variance Reason" value={fr.variance_reason} />}
              {fr.manager_cosigner_name && (
                <>
                  <div className="border-t border-slate-100 my-1" />
                  <div className="flex items-center gap-1.5 text-xs text-emerald-700">
                    <ShieldCheck size={12} />
                    <span>Co-signed by <strong>{fr.manager_cosigner_name}</strong> — Permanently locked.</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 justify-end pb-4">
            <Button variant="outline" onClick={() => window.print()} data-testid="print-x-report">
              <Printer size={14} className="mr-1.5" /> Print X-Report
            </Button>
            <Button
              className="bg-[#1A4D2E] hover:bg-[#14532d] text-white"
              onClick={() => navigate('/dashboard')}
              data-testid="done-close-shift"
            >
              Done
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── WIZARD ─────────────────────────────────────────────────────────────────
  const salesEntries  = preview.sales_entries  || [];
  const creditInvs    = preview.credit_invoices || [];
  const arPayments    = preview.ar_payments     || [];
  const expensesList  = preview.expenses        || [];
  const checkPayments = preview.check_payments  || [];

  const cashSalesTotal    = salesEntries.filter(e => e.payment_type === 'cash').reduce((s, e) => s + (e.grand_total || 0), 0);
  const checkSalesTotal   = salesEntries.filter(e => e.payment_type === 'check').reduce((s, e) => s + (e.grand_total || 0), 0);
  const digitalSalesTotal = salesEntries.filter(e => e.payment_type !== 'cash' && e.payment_type !== 'check').reduce((s, e) => s + (e.grand_total || 0), 0);
  const creditTotal       = creditInvs.reduce((s, e) => s + (e.grand_total || 0), 0);
  const arTotal           = arPayments.reduce((s, e) => s + (e.amount || 0), 0);
  const expTotal          = expensesList.reduce((s, e) => s + (e.amount || 0), 0);
  const checkTotal        = checkPayments.reduce((s, p) => s + (p.amount || 0), 0);

  return (
    <div className="min-h-screen bg-[#F5F5F0] flex flex-col" data-testid="close-shift-page">

      {/* ── Header ── */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <button
            data-testid="close-shift-back"
            onClick={() => navigate('/dashboard')}
            className="p-1.5 rounded-md text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <h1 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Lock size={13} className="text-[#1A4D2E]" />
              Close Shift
            </h1>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {preview.cashier_name} · {preview.shift_id} · Opened {fmtTime(preview.opened_at)}
            </p>
          </div>
        </div>
        <Badge className="bg-slate-100 text-slate-600 text-[10px]">
          {step}/{STEPS.length}
        </Badge>
      </div>

      {/* ── Body: sidebar + content ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* Sidebar — desktop only */}
        <aside className="hidden md:flex flex-col w-56 shrink-0 bg-white border-r border-slate-200 py-3 overflow-y-auto">
          {STEPS.map(s => {
            const isCur  = s.id === step;
            const isPast = step > s.id;
            return (
              <button
                key={s.id}
                data-testid={`step-nav-${s.id}`}
                onClick={() => isPast && setStep(s.id)}
                className={`flex items-start gap-2.5 px-3 py-2.5 text-left transition-colors w-full ${
                  isCur  ? 'bg-emerald-50 border-r-2 border-[#1A4D2E]' :
                  isPast ? 'hover:bg-slate-50 cursor-pointer' :
                           'opacity-40 cursor-not-allowed'
                }`}
              >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 text-[11px] font-bold ${
                  isCur  ? 'bg-[#1A4D2E] text-white' :
                  isPast ? 'bg-emerald-100 text-emerald-700' :
                           'bg-slate-100 text-slate-400'
                }`}>
                  {isPast ? '✓' : s.id}
                </div>
                <div className="min-w-0">
                  <p className={`text-xs font-medium leading-tight ${isCur ? 'text-[#1A4D2E]' : isPast ? 'text-slate-600' : 'text-slate-400'}`}>
                    {s.title}
                  </p>
                  <p className="text-[10px] text-slate-400 leading-tight mt-0.5">{s.desc}</p>
                </div>
              </button>
            );
          })}
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto p-4 md:p-6 space-y-4">

            {/* ── STEP 1: Cash Sales ──────────────────────────────── */}
            {step === 1 && (
              <>
                <StepHeader step={1} title="Cash Sales This Shift" />
                <div className="grid grid-cols-3 gap-3">
                  <StatCard label="Cash Sales"    value={`₱${fmt(cashSalesTotal)}`}    color="emerald" />
                  <StatCard label="Digital Sales" value={`₱${fmt(digitalSalesTotal)}`} color="blue"    />
                  {checkSalesTotal > 0
                    ? <StatCard label="Check Sales" value={`₱${fmt(checkSalesTotal)}`} color="teal" />
                    : <StatCard label="Total Invoices" value={salesEntries.length} color="slate" />
                  }
                </div>
                {Object.keys(preview.sales_by_payment_type || {}).length > 0 && (
                  <SectionCard title="Sales by Payment Type">
                    <div className="space-y-1.5">
                      {Object.entries(preview.sales_by_payment_type || {}).map(([type, data]) => (
                        <div key={type} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <PMBadge type={type} />
                            <span className="text-slate-500 text-xs">{data.count} invoice{data.count !== 1 ? 's' : ''}</span>
                          </div>
                          <span className="font-semibold">₱{fmt(data.total)}</span>
                        </div>
                      ))}
                      <div className="border-t border-slate-100 pt-2 flex justify-between font-semibold text-sm">
                        <span className="text-slate-700">Total Gross Sales</span>
                        <span className="text-[#1A4D2E]">₱{fmt(preview.total_gross_sales)}</span>
                      </div>
                    </div>
                  </SectionCard>
                )}
                <SectionCard title={`Invoice List (${salesEntries.length})`}>
                  {salesEntries.length === 0 ? (
                    <p className="text-xs text-slate-400 py-2 text-center">No cash or digital sales this shift.</p>
                  ) : (
                    <div className="overflow-x-auto -mx-3">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-slate-100">
                            <th className="text-left px-3 py-1.5 text-slate-500 font-medium">Invoice #</th>
                            <th className="text-left px-3 py-1.5 text-slate-500 font-medium">Customer</th>
                            <th className="text-left px-3 py-1.5 text-slate-500 font-medium">Type</th>
                            <th className="text-right px-3 py-1.5 text-slate-500 font-medium">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {salesEntries.map((e, i) => (
                            <tr key={i} className={`border-b border-slate-50 hover:bg-slate-50 ${e.payment_type === 'check' ? 'bg-cyan-50/30' : ''}`}>
                              <td className="px-3 py-1.5 text-slate-600">{e.invoice_number}</td>
                              <td className="px-3 py-1.5 text-slate-700">{e.customer_name || 'Walk-in'}</td>
                              <td className="px-3 py-1.5"><PMBadge type={e.payment_type} /></td>
                              <td className="px-3 py-1.5 text-right font-semibold">₱{fmt(e.grand_total)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 border-slate-200 bg-slate-50">
                            <td colSpan={3} className="px-3 py-1.5 text-xs font-semibold text-slate-700">Total Gross Sales</td>
                            <td className="px-3 py-1.5 text-right font-bold text-[#1A4D2E]">₱{fmt(preview.total_gross_sales)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </SectionCard>
              </>
            )}

            {/* ── STEP 2: Credit Generated ────────────────────────── */}
            {step === 2 && (
              <>
                <StepHeader step={2} title="Customer Credit Generated This Shift" />
                <div className="grid grid-cols-2 gap-3">
                  <StatCard label="Credit Created"   value={`₱${fmt(creditTotal)}`} color="amber" />
                  <StatCard label="Credit Invoices"  value={creditInvs.length}       color="slate" />
                </div>
                <SectionCard title={`Credit Invoice List (${creditInvs.length})`}>
                  {creditInvs.length === 0 ? (
                    <p className="text-xs text-slate-400 py-2 text-center">No credit or partial-credit invoices this shift.</p>
                  ) : (
                    <div className="overflow-x-auto -mx-3">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-slate-100">
                            <th className="text-left px-3 py-1.5 text-slate-500 font-medium">Invoice #</th>
                            <th className="text-left px-3 py-1.5 text-slate-500 font-medium">Customer</th>
                            <th className="text-left px-3 py-1.5 text-slate-500 font-medium">Type</th>
                            <th className="text-right px-3 py-1.5 text-slate-500 font-medium">Amount</th>
                            <th className="text-right px-3 py-1.5 text-slate-500 font-medium">Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {creditInvs.map((e, i) => (
                            <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                              <td className="px-3 py-1.5 text-slate-600">{e.invoice_number}</td>
                              <td className="px-3 py-1.5 text-slate-700">{e.customer_name || '—'}</td>
                              <td className="px-3 py-1.5"><PMBadge type={e.payment_type} /></td>
                              <td className="px-3 py-1.5 text-right font-semibold">₱{fmt(e.grand_total)}</td>
                              <td className="px-3 py-1.5 text-right text-amber-700">₱{fmt(e.credit_balance)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 border-amber-200 bg-amber-50/40">
                            <td colSpan={3} className="px-3 py-1.5 text-xs font-semibold text-amber-800">Total Credit Created</td>
                            <td className="px-3 py-1.5 text-right font-bold text-amber-800">₱{fmt(creditTotal)}</td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </SectionCard>
              </>
            )}

            {/* ── STEP 3: AR Payments ─────────────────────────────── */}
            {step === 3 && (
              <>
                <StepHeader step={3} title="AR / Credit Payments Collected This Shift" />
                <div className="grid grid-cols-2 gap-3">
                  <StatCard label="AR Collected"  value={`₱${fmt(arTotal)}`} color="teal"  />
                  <StatCard label="Transactions"  value={arPayments.length}   color="slate" />
                </div>
                <SectionCard title={`AR Payment List (${arPayments.length})`}>
                  {arPayments.length === 0 ? (
                    <p className="text-xs text-slate-400 py-2 text-center">No AR / credit payments collected this shift.</p>
                  ) : (
                    <div className="overflow-x-auto -mx-3">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-slate-100">
                            <th className="text-left px-3 py-1.5 text-slate-500 font-medium">Reference</th>
                            <th className="text-left px-3 py-1.5 text-slate-500 font-medium">Method</th>
                            <th className="text-right px-3 py-1.5 text-slate-500 font-medium">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {arPayments.map((m, i) => (
                            <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                              <td className="px-3 py-1.5 text-slate-700 max-w-xs truncate">{m.reference || '—'}</td>
                              <td className="px-3 py-1.5">
                                <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${m.payment_method === 'check' ? 'bg-cyan-100 text-cyan-700' : m.type === 'cash_in' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                                  {m.payment_method === 'check' ? 'Check' : m.type === 'cash_in' ? 'Cash' : m.platform || 'Digital'}
                                </span>
                              </td>
                              <td className="px-3 py-1.5 text-right font-semibold text-teal-700">₱{fmt(m.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 border-teal-200 bg-teal-50/40">
                            <td colSpan={2} className="px-3 py-1.5 text-xs font-semibold text-teal-800">Total AR Collected</td>
                            <td className="px-3 py-1.5 text-right font-bold text-teal-800">₱{fmt(arTotal)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </SectionCard>

                {/* Check Payments This Shift */}
                {(() => {
                  const checkPmts = preview.check_payments || [];
                  if (checkPmts.length === 0) return null;
                  const checkTotal = checkPmts.reduce((s, p) => s + (p.amount || 0), 0);
                  return (
                    <SectionCard title={`Check Payments This Shift (${checkPmts.length})`}>
                      <div className="text-[10px] text-slate-500 mb-2 flex items-center gap-1">
                        <span className="inline-flex px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-700 font-medium">Check</span>
                        <span>— treated as cash in drawer. Verify physically before closing.</span>
                      </div>
                      <div className="overflow-x-auto -mx-3">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-slate-100">
                              <th className="text-left px-3 py-1.5 text-slate-500 font-medium">Customer</th>
                              <th className="text-left px-3 py-1.5 text-slate-500 font-medium">Origin</th>
                              <th className="text-left px-3 py-1.5 text-slate-500 font-medium">Check #</th>
                              <th className="text-left px-3 py-1.5 text-slate-500 font-medium">Date</th>
                              <th className="text-right px-3 py-1.5 text-slate-500 font-medium">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {checkPmts.map((p, i) => (
                              <tr key={i} className="border-b border-slate-50 hover:bg-cyan-50/30">
                                <td className="px-3 py-1.5 text-slate-700">{p.customer_name || (p.reference?.split('—')[1]?.trim()) || '—'}</td>
                                <td className="px-3 py-1.5">
                                  <span className={`inline-flex px-1 py-px rounded text-[9px] font-medium ${p.origin === 'sale' ? 'bg-emerald-100 text-emerald-700' : 'bg-teal-100 text-teal-700'}`}>
                                    {p.origin === 'sale' ? 'Sale' : 'AR'}
                                  </span>
                                </td>
                                <td className="px-3 py-1.5 font-mono text-slate-600">{p.check_number || <span className="text-slate-300">—</span>}</td>
                                <td className="px-3 py-1.5 text-slate-500">{p.check_date || '—'}</td>
                                <td className="px-3 py-1.5 text-right font-semibold text-cyan-700">₱{fmt(p.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-t-2 border-cyan-200 bg-cyan-50/40">
                              <td colSpan={4} className="px-3 py-1.5 text-xs font-semibold text-cyan-800">Total Check Payments — verify checks are in drawer</td>
                              <td className="px-3 py-1.5 text-right font-bold text-cyan-800">₱{fmt(checkTotal)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </SectionCard>
                  );
                })()}
              </>
            )}

            {/* ── STEP 4: Expenses ────────────────────────────────── */}
            {step === 4 && (
              <>
                <StepHeader step={4} title="Expenses This Shift" />
                <div className="grid grid-cols-2 gap-3">
                  <StatCard label="Total Expenses"  value={`₱${fmt(expTotal)}`}   color="rose"  />
                  <StatCard label="Expense Entries" value={expensesList.length}    color="slate" />
                </div>
                <SectionCard title={`Expense List (${expensesList.length})`}>
                  {expensesList.length === 0 ? (
                    <p className="text-xs text-slate-400 py-2 text-center">No expenses recorded this shift.</p>
                  ) : (
                    <div className="overflow-x-auto -mx-3">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-slate-100">
                            <th className="text-left px-3 py-1.5 text-slate-500 font-medium">Description</th>
                            <th className="text-left px-3 py-1.5 text-slate-500 font-medium">Category</th>
                            <th className="text-right px-3 py-1.5 text-slate-500 font-medium">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {expensesList.map((e, i) => (
                            <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                              <td className="px-3 py-1.5 text-slate-700 max-w-xs truncate">{e.description || '—'}</td>
                              <td className="px-3 py-1.5 text-slate-500">{e.category || '—'}</td>
                              <td className="px-3 py-1.5 text-right font-semibold text-rose-700">₱{fmt(e.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 border-rose-200 bg-rose-50/40">
                            <td colSpan={2} className="px-3 py-1.5 text-xs font-semibold text-rose-800">Total Expenses</td>
                            <td className="px-3 py-1.5 text-right font-bold text-rose-800">₱{fmt(expTotal)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </SectionCard>
              </>
            )}

            {/* ── STEP 5: Blind Count ─────────────────────────────── */}
            {step === 5 && (
              <>
                <StepHeader step={5} title="Actual Cash Count" />
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-start gap-2.5" data-testid="blind-count-banner">
                  <EyeOff size={15} className="text-amber-700 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-900 leading-snug">
                    <strong>Blind count.</strong> Count the physical cash in your drawer <em>before</em> seeing what
                    the system expects. Your count locks before the variance is revealed.
                  </p>
                </div>

                <SectionCard title="Cash Drawer Components">
                  <div className="space-y-1">
                    <CRow label="Opening float"         value={`₱${fmt(preview.cash_breakdown?.opening_float)}`} />
                    <CRow label="Cash sales"             value={`₱${fmt(preview.cash_breakdown?.sales_cash)}`} />
                    {(preview.cash_breakdown?.ar_collections_cash || 0) > 0 && (
                      <CRow label="AR / credit collections" value={`₱${fmt(preview.cash_breakdown?.ar_collections_cash)}`} />
                    )}
                    {checkTotal > 0 && (
                      <CRow label="↳ of which by check (verify vs drawer)" value={`₱${fmt(checkTotal)}`} cyan />
                    )}
                    {(preview.cash_breakdown?.drops || 0) !== 0 && (
                      <CRow label="Cash drops (removed)" value={`−₱${fmt(Math.abs(preview.cash_breakdown?.drops || 0))}`} red />
                    )}
                    {(preview.cash_breakdown?.returns || 0) !== 0 && (
                      <CRow label="Returns / Refunds" value={`−₱${fmt(Math.abs(preview.cash_breakdown?.returns || 0))}`} red />
                    )}
                    {(preview.cash_breakdown?.other || 0) !== 0 && (
                      <CRow label="Other" value={`₱${fmt(preview.cash_breakdown?.other)}`} />
                    )}
                    <div className="border-t border-amber-100 mt-1.5 pt-1.5 flex items-center gap-1.5 text-xs text-amber-700">
                      <EyeOff size={10} />
                      <span>Expected total hidden until after your count.</span>
                    </div>
                  </div>
                </SectionCard>

                <div className="space-y-2">
                  <Label htmlFor="cash-counted" className="flex items-center gap-1.5 text-sm font-medium">
                    <Calculator size={13} className="text-slate-500" /> Counted Cash (₱)
                  </Label>
                  <Input
                    id="cash-counted"
                    data-testid="cash-counted-input"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={cashCounted}
                    onChange={e => { setCashCounted(e.target.value); setVarianceRevealed(false); setReason(''); }}
                    className="text-lg h-12"
                    autoFocus
                  />
                  {cashCounted !== '' && !varianceRevealed && (
                    <Button
                      variant="outline"
                      className="w-full border-amber-300 text-amber-800 hover:bg-amber-50"
                      data-testid="reveal-variance-btn"
                      onClick={() => setVarianceRevealed(true)}
                    >
                      Lock count &amp; reveal variance →
                    </Button>
                  )}
                </div>

                {varianceRevealed && (
                  <div className="space-y-3">
                    <div className={`rounded-lg border p-3 ${vcBg}`}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-600">System expected</span>
                        <span>₱{fmt(expectedCash)}</span>
                      </div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-slate-600">You counted</span>
                        <span>₱{fmt(counted)}</span>
                      </div>
                      <div className="border-t border-slate-200 pt-2 flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-700">Variance</span>
                        <span className={`text-lg font-bold  ${vc}`} data-testid="variance-display">
                          {vcLabel}
                        </span>
                      </div>
                    </div>
                    {variance !== 0 && (
                      <div className="space-y-1.5">
                        <Label htmlFor="variance-reason" className="flex items-center gap-1.5 text-amber-700 text-xs font-medium">
                          <AlertTriangle size={12} /> Reason for variance (required)
                        </Label>
                        <Textarea
                          id="variance-reason"
                          data-testid="variance-reason-input"
                          placeholder="e.g., Short change on invoice SI-001; refund from personal funds"
                          value={reason}
                          onChange={e => setReason(e.target.value)}
                          rows={2}
                        />
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* ── STEP 6: Summary ─────────────────────────────────── */}
            {step === 6 && (
              <>
                <StepHeader step={6} title="Shift Summary — Pre-Close Review" />
                <div className="rounded-lg border-[var(--border-default)] bg-white overflow-hidden">
                  <div className="bg-[#1A4D2E]/5 px-4 py-3 border-b border-slate-200">
                    <p className="text-sm font-semibold text-[#1A4D2E]">{preview.cashier_name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {preview.shift_id} · {fmtTime(preview.opened_at)} — now
                    </p>
                    {preview.opening_authorized_by_name && (
                      <p className="text-[10px] text-slate-400 mt-0.5">Float authorized by {preview.opening_authorized_by_name}</p>
                    )}
                  </div>
                  <div className="p-4 space-y-1.5">
                    <SumRow label="Opening Float"    value={`₱${fmt(preview.opening_float)}`} />
                    <SumRow label="Gross Sales"       value={`₱${fmt(preview.total_gross_sales)}`}  bold />
                    <SumRow label="Total Invoices"    value={preview.total_invoice_count} />
                    {cashSalesTotal > 0 && <SumRow label="↳ Cash Sales"    value={`₱${fmt(cashSalesTotal)}`}    indent />}
                    {checkSalesTotal > 0 && <SumRow label="↳ Check Sales"  value={`₱${fmt(checkSalesTotal)}`}   indent cyan />}
                    {(preview.epay_grand_total || 0) > 0 && <SumRow label="↳ Digital"  value={`₱${fmt(preview.epay_grand_total)}`}  indent />}
                    {creditTotal > 0    && <SumRow label="Credit Created"  value={`₱${fmt(creditTotal)}`} />}
                    {arTotal > 0        && <SumRow label="AR Collected"    value={`+₱${fmt(arTotal)}`}    teal />}
                    {checkTotal > 0     && <SumRow label="↳ of which Check" value={`₱${fmt(checkTotal)}`} indent cyan />}
                    {expTotal > 0       && <SumRow label="Expenses"        value={`−₱${fmt(expTotal)}`}   rose />}
                    <div className="border-t border-slate-200 pt-2 mt-1 space-y-1.5">
                      <SumRow label="Expected Cash in Drawer" value={`₱${fmt(expectedCash)}`} bold />
                      <SumRow label="Your Count"              value={`₱${fmt(counted)}`} />
                    </div>
                    <div className={`rounded-lg border px-3 py-2.5 mt-1 flex items-center justify-between ${vcBg}`}>
                      <span className="text-sm font-semibold text-slate-800">Variance</span>
                      <span className={`text-lg font-bold  ${vc}`} data-testid="summary-variance">
                        {vcLabel}
                      </span>
                    </div>
                    {reason && (
                      <div className="text-xs text-slate-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                        <strong>Variance reason:</strong> {reason}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* ── STEP 7: Close & Lock ────────────────────────────── */}
            {step === 7 && (
              <>
                <StepHeader step={7} title="Close & Lock Shift" />

                {/* Mini summary */}
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <div className="bg-slate-50 px-3 py-2 border-b border-slate-200 flex items-center justify-between">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">Closing Summary</span>
                    <span className="text-[11px] text-slate-500">{preview.cashier_name}</span>
                  </div>
                  <div className="px-3 py-2.5 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Expected</span>
                      <span>₱{fmt(expectedCash)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Counted</span>
                      <span>₱{fmt(counted)}</span>
                    </div>
                    <div className="border-t border-slate-200 pt-1.5 flex justify-between font-bold">
                      <span>Variance</span>
                      <span className={vc}>{vcLabel}</span>
                    </div>
                  </div>
                </div>

                {reason && (
                  <div className="text-xs text-slate-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    <strong>Variance reason:</strong> {reason}
                  </div>
                )}

                {/* Dual-sig banner */}
                <div className="rounded-lg bg-blue-50/50 border border-blue-200 px-3 py-2.5 flex items-start gap-2">
                  <Users size={14} className="text-blue-700 mt-0.5 shrink-0" />
                  <p className="text-xs text-blue-900 leading-snug">
                    <strong>Dual-signature required.</strong> Both you and your manager must enter PINs.
                    Once closed, this shift is <strong>permanently locked</strong>.
                  </p>
                </div>

                {/* PIN inputs */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="cashier-pin" className="flex items-center gap-1.5 text-xs font-medium">
                      <KeyRound size={11} /> Your PIN
                    </Label>
                    <Input
                      id="cashier-pin"
                      data-testid="cashier-pin-input"
                      type="password"
                      placeholder="••••"
                      value={pin}
                      onChange={e => setPin(e.target.value)}
                      autoComplete="one-time-code" data-lpignore="true"
                      className="text-center font-mono tracking-widest"
                      autoFocus
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="manager-pin" className="flex items-center gap-1.5 text-xs font-medium">
                      <ShieldCheck size={11} className="text-emerald-700" /> Manager PIN
                    </Label>
                    <Input
                      id="manager-pin"
                      data-testid="manager-pin-input"
                      type="password"
                      placeholder="Witness PIN"
                      value={managerPin}
                      onChange={e => setManagerPin(e.target.value)}
                      autoComplete="one-time-code" data-lpignore="true"
                      className="text-center font-mono tracking-widest"
                    />
                  </div>
                </div>
                {pin && managerPin && pin === managerPin && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertTriangle size={11} /> Manager PIN must differ from cashier PIN.
                  </p>
                )}
              </>
            )}

          </div>
        </main>
      </div>

      {/* ── Footer Navigation ── */}
      <div className="bg-white border-t border-slate-200 px-4 py-3 flex items-center justify-between shrink-0">
        <div>
          {step > 1 ? (
            <Button variant="outline" size="sm" onClick={() => setStep(s => s - 1)} data-testid="wizard-back">
              ← Back
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-slate-500" data-testid="cancel-close">
              Cancel
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Mobile step label */}
          <span className="text-xs text-slate-400 md:hidden">{STEPS[step - 1]?.title}</span>
          {step < 7 && (
            <Button
              size="sm"
              data-testid="wizard-next"
              onClick={() => setStep(s => s + 1)}
              disabled={step === 5 && !canStep5Proceed}
              className="bg-[#1A4D2E] hover:bg-[#14532d] text-white"
            >
              {step === 6 ? 'Proceed to Sign-Off →' : `Next: ${STEPS[step]?.title ?? ''} →`}
            </Button>
          )}
          {step === 7 && (
            <Button
              size="sm"
              data-testid="confirm-close-shift"
              onClick={submit}
              disabled={
                submitting ||
                !pin.trim() ||
                !managerPin.trim() ||
                pin === managerPin ||
                (variance !== 0 && !reason.trim())
              }
              className="bg-[#1A4D2E] hover:bg-[#14532d] text-white"
            >
              {submitting ? 'Closing…' : 'Close & Lock Shift'}
            </Button>
          )}
        </div>
      </div>

    </div>
  );
}

// ── Shared helpers ─────────────────────────────────────────────────────────

function StepHeader({ step, title }) {
  return (
    <div className="flex items-center gap-2.5 pb-1">
      <div className="w-7 h-7 rounded-full bg-[#1A4D2E] text-white text-xs font-bold flex items-center justify-center shrink-0">
        {step}
      </div>
      <h2 className="text-base font-semibold text-slate-800">{title}</h2>
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <div className="bg-slate-50 px-3 py-1.5 border-b border-slate-200">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{title}</span>
      </div>
      <div className="px-3 py-2.5">{children}</div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  const bg   = { emerald: 'bg-emerald-50', blue: 'bg-blue-50', slate: 'bg-slate-50', amber: 'bg-amber-50', teal: 'bg-teal-50', rose: 'bg-rose-50' };
  const text = { emerald: 'text-emerald-700', blue: 'text-blue-700', slate: 'text-slate-700', amber: 'text-amber-700', teal: 'text-teal-700', rose: 'text-rose-700' };
  return (
    <div className={`rounded-lg border border-slate-200 ${bg[color] || 'bg-slate-50'} p-3`}>
      <p className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`text-sm font-bold  mt-0.5 ${text[color] || 'text-slate-700'}`}>{value}</p>
    </div>
  );
}

function CRow({ label, value, red, cyan }) {
  return (
    <div className="flex justify-between text-xs py-0.5">
      <span className="text-slate-600">{label}</span>
      <span className={` ${red ? 'text-red-600' : cyan ? 'text-cyan-700' : 'text-slate-800'}`}>{value}</span>
    </div>
  );
}

function XRow({ label, value, bold }) {
  return (
    <div className="flex items-start justify-between gap-2">
      <span className="text-slate-500 text-xs shrink-0">{label}</span>
      <span className={`${bold ? 'font-semibold text-slate-900' : 'text-slate-700'} text-xs text-right break-words`}>{value}</span>
    </div>
  );
}

function SumRow({ label, value, bold, indent, teal, rose, cyan }) {
  return (
    <div className="flex justify-between text-sm">
      <span className={`${indent ? 'pl-3 text-xs' : ''} text-slate-600`}>{label}</span>
      <span className={` ${bold ? 'font-semibold text-slate-900' : ''} ${teal ? 'text-teal-700' : ''} ${rose ? 'text-rose-700' : ''} ${cyan ? 'text-cyan-700' : ''}`}>
        {value}
      </span>
    </div>
  );
}
